import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { trimObject, checkNonMoneyDataForTable } from "utils/utils";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import CreateContent from "./components/CreateContent/CreateContent";
import s from "./ByTransactionDate.module.scss";
import { queryList, preSign, deleteFile, createFile } from "./model";

const ByTransactionDate = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "byTransactionDateGrid";
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
  });
  const [search, setSearch] = useState(false);
  const [searchFieldInput, setSearchFieldInput] = useState({
    key: "",
  });
  const [focus, setFocus] = useState({});
  const [columnsArray, setColumnsArray] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteDialogData, setDeleteDialogData] = useState({
    key: "",
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDialogData, setCreateDialogData] = useState({});

  useEffect(() => {
    setColumnsArray([...columns]);
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryList({});
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.reconciliation.sectionName")}` }), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            const first200Result = {
              ...responseBody,
              data: responseBody.data.slice(0, 20),
            };
            setDatasource(first200Result);
          } else {
            snackbar(
              t("general.message.search.error") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      key: "",
    });
  };

  const handleReset = async () => {
    resetInputField();
    await initialFetch();
  };

  const handleSearch = async () => {
    setSearching(true);
    const { key } = searchFieldInput;
    const newKey = "reconciliation/csv/Reconciliation-" + key;
    const payload = trimObject({ key: newKey });
    const apiResponse = await queryList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.reconciliation.sectionName")}` }), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            const first200Result = {
              ...responseBody,
              data: responseBody.data.slice(0, 20),
            };
            setDatasource(first200Result);
          } else {
            snackbar(
              t("general.message.search.error") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };

  const handleDownload = async (item) => {
    const payload = { key: item.Key };
    const apiResponse = await preSign(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.download.function.success.message"),
              "success",
            );
            if (responseBody?.data?.url) {
              window.location = responseBody?.data?.url;
            }
          } else {
            snackbar(
              t("general.message.download.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
  };

  // ===================== delete funciton ===================== //
  const openDeleteDialog = (data) => {
    setDeleteDialogData({
      key: data.Key,
    });
    setIsDeleteDialogOpen(true);
    setDialogLoading(false);
  };

  const closeDeleteDialog = () => {
    setDialogLoading(false);
    setIsDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    const payload = trimObject({ ...deleteDialogData });
    const apiResponse = await deleteFile(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.delete.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.delete.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmDelete = async () => {
    setDialogLoading(true);
    const result = await handleDelete();
    closeDeleteDialog();
    if (result) {
      return handleSearch();
    }
  };

  // ===================== create funciton ===================== //
  const openCreateDialog = () => {
    setIsCreateDialogOpen(true);
    setDialogLoading(false);
  };

  const closeCreateDialog = () => {
    setDialogLoading(false);
    restoreCreateFormInput();
    setIsCreateDialogOpen(false);
  };

  const handleCreateFormInput = (data, field) => {
    setCreateDialogData({
      ...createDialogData,
      [field]: data,
    });
  };

  const restoreCreateFormInput = () => {
    setCreateDialogData({});
  };

  const createContent = () => {
    return (
      <CreateContent
        handleInput={handleCreateFormInput}
        data={createDialogData}
      />
    );
  };

  const createReconciliationFile = async () => {
    const payload = trimObject({ ...createDialogData });
    const apiResponse = await createFile(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.create.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.create.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmCreate = async () => {
    if (!createDialogData.transDate) {
      snackbar(
        t(
          "page.finance.reconciliation.by.transaction.date.create.function.error.message.1",
        ),
        "warning",
      );
      return;
    }
    setDialogLoading(true);
    const result = await createReconciliationFile();
    closeCreateDialog();
    if (result) {
      return handleSearch();
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const columns = [
    {
      index: 0,
      key: "Key",
      title: t(
        "page.finance.reconciliation.by.transaction.date.column.title.key",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "LastModified",
      title: t(
        "page.finance.reconciliation.by.transaction.date.column.title.created",
      ),
      sorter: false,
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD HH:mm");
      },
    },
    {
      index: 2,
      key: "action",
      title: t(
        "page.finance.reconciliation.by.transaction.date.column.title.action",
      ),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS variant="text-only" onClick={() => handleDownload(data)}>
              {t(
                "page.finance.reconciliation.by.transaction.date.column.title.action.1",
              )}
            </ButtonMAS>
            <ButtonMAS variant="text-only" onClick={() => openDeleteDialog(data)}>
              {t(
                "page.finance.reconciliation.by.transaction.date.column.title.action.2",
              )}
            </ButtonMAS>
          </div>
        );
      },
    },
  ];

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={undefined}>
          <OutlinedText
            label={t(
              "page.finance.reconciliation.by.transaction.date.key.label",
            )}
            placeholder={t(
              "page.finance.reconciliation.by.transaction.date.key.placeholder",
            )}
            value={searchFieldInput.key}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "key")}
            onfocusFn={() => textInputOnFocus("key")}
            onblurFn={() => textInputOnBlur("key")}
            sxParams={{ textFocus: focus.key }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>
              {t("page.finance.reconciliation.by.transaction.date.remarks.1")}
            </span>
          </div>
          <FunctionalButtonBar
            loading={loading || searching}
            createBtn
            handleCreate={openCreateDialog}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columnsArray}
              datasource={datasource.data}
              search={search}
            />
          )}
          {searching && <Spin type={"search"} />}
        </div>
        <SimpleDialog
          open={isDeleteDialogOpen}
          dialogOncloseFn={closeDeleteDialog}
          title={t(
            "page.finance.reconciliation.by.transaction.date.delete.function.dialog.title",
          )}
          content={t(
            "page.finance.reconciliation.by.transaction.date.delete.function.dialog.content",
          )}
          confirmFn={confirmDelete}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
        <SimpleDialog
          open={isCreateDialogOpen}
          dialogOncloseFn={closeCreateDialog}
          title={t(
            "page.finance.reconciliation.by.transaction.date.create.function.dialog.title",
          )}
          content={createContent()}
          confirmFn={confirmCreate}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
      </div>
    </div>
  );
};

export default React.memo(ByTransactionDate);
